import { useTranslation } from 'react-i18next';
import { useOrderCreateMultiApi, useCalculatePriceApi } from '@hero/api-hooks';
import { useCurrentUser } from '../user/useCurrentUser';
import type { UseOrderAgainConfig } from './useOrderAgain';

export type UseOrderAgainConfigOptions = {
  onCreateSuccess?: () => void;
  onCreateError?: (error: Error) => void;
};

/**
 * Unified wiring for {@link useOrderAgain} — uses ApiProvider
 * defaults from `@hero/api-hooks` (no per-role SDK imports).
 * Works in carrier-dashboard, customer-dashboard, and mobile apps
 * that mount `ApiProvider`.
 */
export function useOrderAgainConfig(
  options: UseOrderAgainConfigOptions = {},
): UseOrderAgainConfig {
  const { orgId } = useCurrentUser();
  const { t } = useTranslation(['m-order']);

  const { mutateAsync: createMultiOrder } = useOrderCreateMultiApi({
    onSuccess: () => {
      options.onCreateSuccess?.();
    },
    onError: (error: Error) => {
      options.onCreateError?.(error);
    },
  });

  const { mutateAsync: calculateMultiPrice } = useCalculatePriceApi({
    invalidationStrategy: 'none',
  });

  return {
    orgId,
    calculateMultiPrice,
    createMultiOrder,
    onError: options.onCreateError,
  };
}

/**
 * Dashboard-friendly wrapper with default success/error toasts.
 * Web apps import this from `@hero/web-hooks`; implementation
 * stays in hooks-shared — pass toast callbacks from the app layer.
 */
export function useOrderAgainConfigWithToasts(
  toast: {
    success: (message: string) => void;
    error: (message: string) => void;
  },
): UseOrderAgainConfig {
  const { t } = useTranslation(['m-order']);

  return useOrderAgainConfig({
    onCreateSuccess: () => {
      toast.success(t('m-order:toast.success.order_created'));
    },
    onCreateError: (error: Error) => {
      toast.error(t('m-order:toast.error.order_create', { message: error.message }));
    },
  });
}
